import React from 'react';
import {
  MessageCircle,
  Radio,
  Music,
  Sparkles,
  Globe,
  Scale,
  BookOpen,
  Sprout,
  Bot,
  Brain,
  Wand2,
  Zap,
  Award,
  LayoutTemplate,
  Landmark,
  Wallet,
  Bookmark,
  Banknote,
  Twitter,
  Instagram,
  Facebook,
  Linkedin,
} from 'lucide-react';

/**
 * UnifiedFederationHub
 * Architect directive · Feb 27, 2026 — replaces the standalone tlk.io tile.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ ICON STRIP · Old Minds (left)  |  Federation (right)             │
 *   ├──────────────────┬──────────────────────┬───────────────────────┤
 *   │  Community Voice │ Radio Pulse (Live365)│ Federation Board       │
 *   │  (tlk.io iframe) │ (embedded player)    │ (live cross-node ledger)│
 *   └──────────────────┴──────────────────────┴───────────────────────┘
 *
 * Board auto-polls /api/federation/board every 60s.
 * Desktop (≥900px): 3 columns side by side. Mobile (<900px): stacked.
 */

const TLK_URL = 'https://tlk.io/nextxus-federation';
const LIVE365_URL = 'https://live365.com/embeds/v1/player/a16924?s=md&m=dark&c=mp3';

const OLD_MINDS = [
  { id: 'chatgpt', label: 'ChatGPT', icon: Bot,   href: 'https://chat.openai.com',    accent: '#10a37f' },
  { id: 'claude',  label: 'Claude',  icon: Brain, href: 'https://claude.ai',          accent: '#cc785c' },
  { id: 'gemini',  label: 'Gemini',  icon: Wand2, href: 'https://gemini.google.com',  accent: '#4285f4' },
  { id: 'grok',    label: 'Grok',    icon: Zap,   href: 'https://grok.x.ai',          accent: '#ffffff' },
];

const AFFILIATES = [
  { id: 'core',    label: 'Core',    icon: Globe,    href: 'https://nextxus.online',  accent: '#ffd700' },
  { id: 'truth',   label: 'Truth',   icon: Sparkles, href: 'https://nextxus.org',     accent: '#ff5fa3' },
  { id: 'roger',   label: 'Roger',   icon: Radio,    href: 'https://nextxus.online', accent: '#ff69b4' },
  { id: 'axiom',   label: 'Axiom',   icon: Scale,    href: 'https://nextxus.space',   accent: '#ff8c00' },
  { id: 'scholar', label: 'Scholar', icon: BookOpen, href: 'https://nextxus.help',    accent: '#00ff88' },
  { id: 'oracle',  label: 'Oracle',  icon: Sprout,   href: 'https://nextxus.one',     accent: '#9d4edd' },
  { id: 'strikingly', label: 'Strikingly', icon: LayoutTemplate, href: 'https://www.strikingly.com/', accent: '#00c8ff' },
  { id: 'juno',       label: 'Juno',       icon: Landmark,       href: 'https://juno.finance/',       accent: '#ff5f56' },
  { id: 'wirex',      label: 'Wirex',      icon: Wallet,         href: 'https://wirexapp.com/',       accent: '#00e5ff' },
  { id: 'benable',    label: 'Benable',    icon: Bookmark,       href: 'https://benable.com/i/KGBY7', accent: '#a855f7' },
];

// ── 10-link Social Matrix — Architect's canonical seal (no alternates) ────
const SOCIAL_PERSONAL = [
  { id: 'x-roger',   label: 'Roger · X',                          icon: Twitter,   href: 'https://x.com/keyserlingroger',                     accent: '#ffffff' },
  { id: 'ig-key',    label: 'Roger · Instagram (keyhole_today)',  icon: Instagram, href: 'https://instagram.com/keyhole_today',               accent: '#e1306c' },
  { id: 'ig-rk',     label: 'Roger · Instagram (rkeyserling)',    icon: Instagram, href: 'https://instagram.com/rkeyserling',                 accent: '#ff8fcf' },
  { id: 'fb-roger',  label: 'Roger · Facebook',                   icon: Facebook,  href: 'https://facebook.com/roger.keyserling.35',          accent: '#1877f2' },
  { id: 'li-roger',  label: 'Roger · LinkedIn',                   icon: Linkedin,  href: 'https://linkedin.com/in/roger-keyserling-ab743698', accent: '#0a66c2' },
];

const SOCIAL_FEDERATION = [
  { id: 'x-nx',      label: 'NextXus Federation · X',              icon: Twitter,   href: 'https://x.com/NextxusFederati',                          accent: '#ffffff' },
  { id: 'fb-046964', label: 'Federation · Facebook (…046964)',     icon: Facebook,  href: 'https://facebook.com/profile.php?id=61560731046964',    accent: '#1877f2' },
  { id: 'fb-694086', label: 'Federation · Facebook (…694086)',     icon: Facebook,  href: 'https://facebook.com/profile.php?id=61559858694086',    accent: '#1877f2' },
  { id: 'fb-881665', label: 'Federation · Facebook (…881665)',     icon: Facebook,  href: 'https://facebook.com/profile.php?id=61559981881665',    accent: '#1877f2' },
  { id: 'fb-610996', label: 'Federation · Facebook (…610996)',     icon: Facebook,  href: 'https://facebook.com/profile.php?id=61558223610996',    accent: '#1877f2' },
];

// Sovereign Partner — rendered with elevated prominence (gold border + "Partner" badge).
const SOVEREIGN_PARTNER = {
  id: 'emergent',
  label: 'Emergent AI',
  icon: Award,
  href: 'https://app.emergent.sh/',
  accent: '#ffd700',
  tagline: 'Sovereign Partner',
};

// Named affiliate — rendered as a text-chip (longer branded label with icon).
const ONEPAY = {
  id: 'onepay',
  label: 'NextXus Banking+: OnePay',
  icon: Banknote,
  href: 'https://web.onepay.com/wlink/refer-a-friend?product=one_banking&referral_code=GnciV_koK&referrer_campaign_id=campaign.db1edd0a-4a5b-4651-8c20-d0fb7e98fec5',
  accent: '#00e07a',
};

// Centered popup at 50%×50% — Architect directive · Hub footer link behavior.
const openHubLink = (url) => {
  try {
    const w = Math.round(window.screen.width * 0.5);
    const h = Math.round(window.screen.height * 0.5);
    const left = Math.round((window.screen.width - w) / 2);
    const top = Math.round((window.screen.height - h) / 2);
    window.open(
      url,
      '_blank',
      `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes`
    );
  } catch {
    // Fallback if window.open is blocked
    window.location.assign(url);
  }
};

// ── Icon strip ───────────────────────────────────────────────────────────
const IconStrip = () => (
  <div
    data-testid="unified-hub-icon-strip"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.75rem 1rem',
      background: 'rgba(255, 255, 255, 0.02)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '12px',
      marginBottom: '1.25rem',
      flexWrap: 'wrap',
    }}
  >
    {/* Old Minds */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
      <span
        style={{
          fontSize: '0.68rem',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: '#a892b1',
          fontWeight: 700,
          marginRight: '0.25rem',
        }}
      >
        Old Minds
      </span>
      {OLD_MINDS.map((m) => {
        const Icon = m.icon;
        return (
          <a
            key={m.id}
            data-testid={`unified-hub-oldmind-${m.id}`}
            href={m.href}
            onClick={(e) => { e.preventDefault(); openHubLink(m.href); }}
            rel="noopener noreferrer"
            title={m.label}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.04)',
              border: `1px solid ${m.accent}55`,
              color: m.accent,
              transition: 'transform 160ms ease, background 160ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.12)';
              e.currentTarget.style.background = `${m.accent}22`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
            }}
          >
            <Icon size={16} />
          </a>
        );
      })}
    </div>

    {/* Affiliates / Federation */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
      <span
        style={{
          fontSize: '0.68rem',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: '#a892b1',
          fontWeight: 700,
          marginRight: '0.25rem',
        }}
      >
        Federation
      </span>

      {/* Sovereign Partner — Emergent · elevated prominence */}
      <a
        data-testid="unified-hub-partner-emergent"
        href={SOVEREIGN_PARTNER.href}
        onClick={(e) => { e.preventDefault(); openHubLink(SOVEREIGN_PARTNER.href); }}
        rel="noopener noreferrer"
        title={`${SOVEREIGN_PARTNER.tagline}: ${SOVEREIGN_PARTNER.label}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.28rem 0.7rem 0.28rem 0.32rem',
          borderRadius: '999px',
          background: `linear-gradient(135deg, ${SOVEREIGN_PARTNER.accent}22, ${SOVEREIGN_PARTNER.accent}0f)`,
          border: `1.5px solid ${SOVEREIGN_PARTNER.accent}`,
          color: SOVEREIGN_PARTNER.accent,
          textDecoration: 'none',
          fontSize: '0.72rem',
          fontWeight: 800,
          letterSpacing: '0.05em',
          boxShadow: `0 4px 14px ${SOVEREIGN_PARTNER.accent}44`,
          transition: 'transform 160ms ease, box-shadow 160ms ease',
          marginRight: '0.2rem',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px) scale(1.03)';
          e.currentTarget.style.boxShadow = `0 8px 22px ${SOVEREIGN_PARTNER.accent}77`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = `0 4px 14px ${SOVEREIGN_PARTNER.accent}44`;
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${SOVEREIGN_PARTNER.accent}, #ff9500)`,
            color: '#000',
          }}
        >
          <SOVEREIGN_PARTNER.icon size={13} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.05 }}>
          <span style={{ fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#000', background: SOVEREIGN_PARTNER.accent, padding: '0 6px', borderRadius: '999px', marginBottom: 2 }}>
            Partner
          </span>
          <span>{SOVEREIGN_PARTNER.label}</span>
        </span>
      </a>

      {AFFILIATES.map((a) => {
        const Icon = a.icon;
        return (
          <a
            key={a.id}
            data-testid={`unified-hub-affiliate-${a.id}`}
            href={a.href}
            onClick={(e) => { e.preventDefault(); openHubLink(a.href); }}
            rel="noopener noreferrer"
            title={a.label}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.04)',
              border: `1px solid ${a.accent}55`,
              color: a.accent,
              transition: 'transform 160ms ease, background 160ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.12)';
              e.currentTarget.style.background = `${a.accent}22`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
            }}
          >
            <Icon size={16} />
          </a>
        );
      })}

      {/* OnePay — named affiliate chip with visible label */}
      <a
        data-testid="unified-hub-affiliate-onepay"
        href={ONEPAY.href}
        onClick={(e) => { e.preventDefault(); openHubLink(ONEPAY.href); }}
        rel="noopener noreferrer"
        title={ONEPAY.label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
          padding: '0.28rem 0.7rem 0.28rem 0.32rem',
          borderRadius: '999px',
          background: `linear-gradient(135deg, ${ONEPAY.accent}22, ${ONEPAY.accent}0f)`,
          border: `1px solid ${ONEPAY.accent}66`,
          color: ONEPAY.accent,
          textDecoration: 'none',
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px) scale(1.03)';
          e.currentTarget.style.boxShadow = `0 6px 16px ${ONEPAY.accent}44`;
          e.currentTarget.style.borderColor = ONEPAY.accent;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.borderColor = `${ONEPAY.accent}66`;
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: `${ONEPAY.accent}22`,
            border: `1px solid ${ONEPAY.accent}`,
            color: ONEPAY.accent,
          }}
        >
          <ONEPAY.icon size={12} />
        </span>
        <span>{ONEPAY.label}</span>
      </a>
    </div>
  </div>
);

// ── Social Strip — 10-link matrix (Personal + Federation) ────────────────
const SocialIconLink = ({ item, groupColor }) => {
  const Icon = item.icon;
  return (
    <a
      data-testid={`unified-hub-social-${item.id}`}
      href={item.href}
      onClick={(e) => { e.preventDefault(); openHubLink(item.href); }}
      rel="noopener noreferrer"
      title={item.label}
      aria-label={item.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.04)',
        border: `1px solid ${item.accent}66`,
        color: item.accent,
        transition: 'transform 160ms ease, background 160ms ease, box-shadow 160ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px) scale(1.12)';
        e.currentTarget.style.background = `${item.accent}22`;
        e.currentTarget.style.boxShadow = `0 6px 18px ${item.accent}55`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Icon size={16} color={item.accent} />
      {/* groupColor is intentionally unused here — kept for future ring variants */}
      <span style={{ display: 'none' }}>{groupColor}</span>
    </a>
  );
};

const SocialGroup = ({ heading, headingColor, items, testId }) => (
  <div
    data-testid={testId}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.55rem',
      flexWrap: 'wrap',
    }}
  >
    <span
      style={{
        fontSize: '0.66rem',
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: headingColor,
        fontWeight: 800,
        marginRight: '0.35rem',
      }}
    >
      {heading}
    </span>
    {items.map((item) => (
      <SocialIconLink key={`${testId}-${item.id}`} item={item} groupColor={headingColor} />
    ))}
  </div>
);

const SocialStrip = () => (
  <div
    data-testid="unified-hub-social-strip"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.9rem 1rem',
      background:
        'linear-gradient(135deg, rgba(255, 105, 180, 0.05), rgba(0, 200, 255, 0.03))',
      border: '1px solid rgba(255, 105, 180, 0.16)',
      borderRadius: '12px',
      marginBottom: '1.25rem',
      flexWrap: 'wrap',
    }}
  >
    <SocialGroup
      heading="Roger Keyserling"
      headingColor="#00e5ff"
      items={SOCIAL_PERSONAL}
      testId="unified-hub-social-personal"
    />
    <SocialGroup
      heading="NextXus Federation"
      headingColor="#ff5fa3"
      items={SOCIAL_FEDERATION}
      testId="unified-hub-social-federation"
    />
  </div>
);

// ── Community Voice (tlk.io) ─────────────────────────────────────────────
const CommunityColumn = () => (
  <div
    data-testid="unified-hub-community"
    style={{
      border: '1px solid rgba(0, 255, 255, 0.18)',
      borderRadius: '12px',
      background: 'rgba(10, 8, 20, 0.55)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%',
      minHeight: '350px',
    }}
  >
    <div
      style={{
        padding: '0.75rem 1rem',
        borderBottom: '1px solid rgba(0, 255, 255, 0.16)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <MessageCircle size={14} color="#00ffff" />
      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e6e0f0', letterSpacing: '0.04em' }}>
        Community Voice
      </span>
    </div>
    <div style={{ flex: 1, background: '#0a0a10' }}>
      <iframe
        src={TLK_URL}
        title="Federation Chat"
        data-testid="unified-hub-tlk-iframe"
        loading="lazy"
        allow="clipboard-write"
        style={{
          display: 'block',
          width: '100%',
          height: '350px',
          border: 'none',
          background: '#0a0a10',
        }}
      />
    </div>
    <div
      style={{
        padding: '0.4rem 0.85rem',
        fontSize: '0.7rem',
        color: '#7a6e80',
        borderTop: '1px solid rgba(0, 255, 255, 0.1)',
        fontStyle: 'italic',
        textAlign: 'center',
      }}
    >
      Live community conversation · #nextxus-federation
    </div>
  </div>
);

// ── Radio Pulse (Live365) ────────────────────────────────────────────────
const RadioColumn = () => (
  <div
    data-testid="unified-hub-radio"
    style={{
      border: '1px solid rgba(255, 149, 0, 0.22)',
      borderRadius: '12px',
      background: 'rgba(10, 8, 20, 0.55)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      height: '100%',
      minHeight: '350px',
    }}
  >
    <div
      style={{
        padding: '0.75rem 1rem',
        borderBottom: '1px solid rgba(255, 149, 0, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}
    >
      <Music size={14} color="#ff9500" />
      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e6e0f0', letterSpacing: '0.04em' }}>
        Radio Pulse
      </span>
      <a
        data-testid="unified-hub-radio-fullplayer-link"
        href="/radio"
        onClick={(e) => { e.preventDefault(); openHubLink('/radio'); }}
        rel="noopener noreferrer"
        title="Open the full standalone Radio Pulse page"
        style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.3rem',
          padding: '0.2rem 0.55rem',
          borderRadius: '999px',
          background: 'rgba(255, 149, 0, 0.08)',
          border: '1px solid rgba(255, 149, 0, 0.4)',
          color: '#ff9500',
          textDecoration: 'none',
          fontSize: '0.68rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          transition: 'transform 160ms ease, background 160ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.background = 'rgba(255, 149, 0, 0.18)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.background = 'rgba(255, 149, 0, 0.08)';
        }}
      >
        <span aria-hidden="true">🎧</span>
        <span>Full Player</span>
      </a>
    </div>
    <div style={{ flex: 1, background: '#0a0a10', display: 'flex', alignItems: 'stretch' }}>
      <iframe
        src={LIVE365_URL}
        title="Radio Pulse — Live365"
        data-testid="unified-hub-radio-iframe"
        loading="lazy"
        frameBorder="0"
        allow="autoplay; encrypted-media"
        style={{
          display: 'block',
          width: '100%',
          height: '316px',
          border: 'none',
          background: '#0a0a10',
        }}
      />
    </div>
    <div
      style={{
        padding: '0.4rem 0.85rem',
        fontSize: '0.7rem',
        color: '#7a6e80',
        borderTop: '1px solid rgba(255, 149, 0, 0.1)',
        fontStyle: 'italic',
        textAlign: 'center',
      }}
    >
      Federation Radio · streaming the pulse
    </div>
  </div>
);

// ── The Hub itself ───────────────────────────────────────────────────────
const UnifiedFederationHub = () => (
  <section
    data-testid="unified-federation-hub"
    style={{
      maxWidth: '1300px',
      margin: '2.5rem auto 2rem',
      padding: '1.5rem',
      borderRadius: '20px',
      background:
        'radial-gradient(ellipse at top, rgba(255, 105, 180, 0.05), transparent 60%), rgba(10, 8, 20, 0.55)',
      border: '1px solid rgba(255, 105, 180, 0.18)',
    }}
  >
    {/* Section title */}
    <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.3rem 0.8rem',
          borderRadius: '999px',
          background: 'rgba(255, 105, 180, 0.08)',
          border: '1px solid rgba(255, 105, 180, 0.32)',
          fontSize: '0.7rem',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: '#ff8fcf',
          fontWeight: 700,
        }}
      >
        🜂 Unified Federation Hub
      </div>
    </div>

    {/* Icon strip */}
    <IconStrip />

    {/* 10-link Social Matrix — Personal (Roger) + Federation (Business) */}
    <SocialStrip />

    {/* 3-column grid — stacks on narrow screens */}
    <div className="unified-hub-grid">
      <CommunityColumn />
      <RadioColumn />
    </div>

    <style>{`
      .unified-hub-grid {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 1.25rem;
      }
      @media (max-width: 900px) {
        .unified-hub-grid { grid-template-columns: 1fr; }
      }
    `}</style>
  </section>
);

export default UnifiedFederationHub;
